import {
	ComponentMetadata,
	ComponentType,
	ScoreDataGridRow,
	UnitMetadata,
} from '@Types';

const NULL_VALUE_KEY = '--';

// Templates already persist unscored values as null (I360A-3171), and the
// score grid highlights a null score as needing one.
const UNSCORED = null as unknown as number;

export type ScoreCarryOver =
	| { keep: false }
	| {
			keep: true;
			metadataType: 'unique' | 'range';
			metadata: ComponentMetadata[];
			nullScore: number;
			rangeClasses: number;
	  };

interface Params {
	// Only a top-level component being reconnected to a new source carries
	// its scores over; multi-param sub-screens are not supported by templates.
	isReconnecting: boolean;
	isParameter: boolean;
	type: ComponentType;
	// The rows on screen before the source changed, including any edits.
	currentRows: ScoreDataGridRow[];
	currentIsUnique: boolean;
	currentUnit?: string;
	// The metadata just fetched for the new source.
	fetched: UnitMetadata;
	// Whether unique metadata gets a '--' (null value) row; not for inspections.
	addNullRow: boolean;
	currentRangeClasses: number;
}

const RESET: ScoreCarryOver = { keep: false };

// The upper boundary in a range key: ' <= 10', '<= 10', '10 <= 20', '> 20'.
const rangeBoundary = (key: string): number =>
	Number.parseFloat(key.trim().split(/\s+/).pop() ?? '');

const carryOverUnique = (
	currentRows: ScoreDataGridRow[],
	fetchedValues: string[],
	addNullRow: boolean,
	currentRangeClasses: number,
): ScoreCarryOver => {
	const scores = new Map<string, number>();
	let nullScore: number | undefined;
	currentRows.forEach(row => {
		if (row.value === NULL_VALUE_KEY) nullScore = row.score;
		else scores.set(row.value, row.score);
	});

	if (!fetchedValues.some(value => scores.has(value))) return RESET;

	// With no '--' row before, assets with no value get no silent 0 either.
	const carriedNullScore = nullScore ?? UNSCORED;
	const metadata = fetchedValues.map(value => ({
		key: value,
		value:
			value === NULL_VALUE_KEY
				? carriedNullScore
				: scores.get(value) ?? UNSCORED,
	}));
	if (addNullRow && !fetchedValues.includes(NULL_VALUE_KEY)) {
		metadata.unshift({ key: NULL_VALUE_KEY, value: carriedNullScore });
	}

	return {
		keep: true,
		metadataType: 'unique',
		metadata,
		nullScore: nullScore ?? 0,
		rangeClasses: currentRangeClasses,
	};
};

const carryOverRange = (
	type: ComponentType,
	currentRows: ScoreDataGridRow[],
	fetched: UnitMetadata,
	currentUnit?: string,
): ScoreCarryOver => {
	const bandRows = currentRows.filter(row => row.value !== NULL_VALUE_KEY);
	if (bandRows.length < 2) return RESET;

	const boundaries = bandRows.map(row => rangeBoundary(row.value));
	if (boundaries.some(Number.isNaN)) return RESET;

	// Proximity bands are distances from the asset, so they hold for any layer.
	if (type !== ComponentType.SpatialProximity) {
		// An unknown current unit is normal for template components (the
		// backend saves none), so only a known unit that changes resets.
		if (currentUnit && currentUnit !== fetched.unit) {
			return RESET;
		}
		const range = fetched.range;
		if (!range) return RESET;
		// The first and last bands are open-ended; data that falls only in
		// one of those is completely outside the bounded part of the bands.
		const lowest = Math.min(...boundaries);
		const highest = Math.max(...boundaries);
		if (range.max <= lowest || range.min > highest) return RESET;
	}

	const nullRow = currentRows.find(row => row.value === NULL_VALUE_KEY);
	return {
		keep: true,
		metadataType: 'range',
		metadata: bandRows.map(row => ({
			key: row.value,
			value: row.score,
			ix: row.id,
		})),
		nullScore: nullRow?.score ?? 0,
		// The two open-ended bands aren't counted as classes.
		rangeClasses: bandRows.length - 2,
	};
};

// When a component's data source or field changes, decide whether its
// current bands and scores still apply to the new data. Anything else
// (including a switch between unique and range data) resets as before.
export const carryOverScores = ({
	isReconnecting,
	isParameter,
	type,
	currentRows,
	currentIsUnique,
	currentUnit,
	fetched,
	addNullRow,
	currentRangeClasses,
}: Params): ScoreCarryOver => {
	if (!isReconnecting || isParameter || currentRows.length === 0) {
		return RESET;
	}

	if (fetched.unique?.length) {
		return currentIsUnique
			? carryOverUnique(
					currentRows,
					fetched.unique,
					addNullRow,
					currentRangeClasses,
			  )
			: RESET;
	}
	if (fetched.range) {
		return currentIsUnique
			? RESET
			: carryOverRange(type, currentRows, fetched, currentUnit);
	}
	return RESET;
};
